import { useEffect, useMemo, useRef } from 'react';
import { Node, Edge } from 'reactflow';
import {
  Schedule,
  Direction,
  LocationGroup,
  ApprovalGroup,
  ResourceRef,
  ScheduleRequirement,
} from '../types';
import { isCyclic } from '../utils/validation';

/**
 * EditorAiCapability — the structural contract the admin AI assistant consumes.
 *
 * It is defined LOCALLY/structurally here (the editor does not depend on the
 * admin app). The admin app duck-types against this shape: a `kind`, a one
 * paragraph `guidelines` string, a list of tool descriptors (JSON-Schema
 * parameters), and an async `invoke(toolName, args)`.
 */
export interface EditorAiTool {
  name: string;
  description: string;
  /** JSON Schema (object) describing the tool arguments. */
  parameters: Record<string, unknown>;
}

export interface EditorToolResult {
  ok: boolean;
  result?: unknown;
  rejected?: boolean;
  reason?: string;
}

export interface EditorAiCapability {
  kind: string;
  guidelines: string;
  tools: EditorAiTool[];
  invoke: (toolName: string, args: Record<string, unknown>) => Promise<EditorToolResult>;
}

/**
 * Live model accessors + pure builders + the single apply path.
 *
 * The capability NEVER mutates the editor directly. Instead it:
 *   1. reads the latest model (getNodes/getEdges — ref-backed, never stale),
 *   2. builds a CANDIDATE next model with the pure builders,
 *   3. runs the editor's REAL validation (validateGraph) on that candidate, and
 *   4. only then commits via applyModel (the editor's real setNodes/setEdges).
 *
 * If a mutation would introduce a new validation error the capability rejects
 * with the validator's reason and applies nothing.
 */
export interface AiCapabilityDeps {
  /** Latest reactflow nodes (each node.data is a Schedule). */
  getNodes: () => Node<Schedule>[];
  /** Latest reactflow edges (parent -> child links). */
  getEdges: () => Edge[];
  /** Pure: build a fully-defaulted schedule Node from partial input (no state change). */
  buildScheduleNode: (partial: Partial<Schedule>) => Node<Schedule>;
  /** Pure: build a parent -> child edge (no state change). */
  makeEdge: (source: string, target: string) => Edge;
  /** Commit a validated candidate model through the editor's real mutation path. */
  applyModel: (nodes: Node<Schedule>[], edges: Edge[]) => void;

  // --- Optional viewport / selection deps (non-mutating UI affordances) ---
  // These route through the editor's REAL reactflow instance + selection state.
  // They are optional so a host that builds the capability without a live
  // canvas (e.g. headless tests) still gets the model tools; viewport/selection
  // tools then reject gracefully instead of throwing.

  /** Current reactflow viewport { x, y, zoom }. */
  getViewport?: () => { x: number; y: number; zoom: number };
  /** Pan/zoom to an exact viewport (animated when duration > 0). */
  setViewport?: (vp: { x: number; y: number; zoom: number }, opts?: { duration?: number }) => void;
  /** Center the canvas on a world coordinate at a zoom level. */
  setCenter?: (x: number, y: number, opts?: { zoom?: number; duration?: number }) => void;
  /** Fit the given nodes (or the whole graph when nodes is omitted) into view. */
  fitView?: (opts?: { nodes?: { id: string }[]; padding?: number; duration?: number; maxZoom?: number }) => void;
  /** Replace the reactflow node/edge `selected` flags (multi-select highlight). */
  setSelection?: (nodeIds: string[], edgeIds: string[]) => void;
  /** Open / close the details panel for a schedule (null = close). */
  openDetailsFor?: (schedule: Schedule | null) => void;
  /** Run the editor's real auto-layout (dagre). */
  runLayout?: () => void;
  /** Min/max zoom the canvas clamps to (defaults 0.1 / 1.5). */
  zoomBounds?: { min: number; max: number };
}

const KIND = 'entitlement';

const GUIDELINES =
  "This editor models an Entitlement (Title) as a directed acyclic graph of " +
  "Schedule nodes. There is exactly one node/element type: 'schedule' (a permission " +
  "schedule with scheduleId, name, time, group, purpose, priority, direction " +
  "(0=BottomUp/inherits from parents, 1=TopDown/pushes to children), resources, " +
  "approvalGroups, locationGroup, requirements, tags). Edges are parent->child links " +
  "(source=parent, target=child) and the graph MUST stay acyclic — connecting two " +
  "nodes that would form a loop is rejected. parents/children on a schedule are derived " +
  "from edges and must not be set directly. Use add_schedule/update_schedule/" +
  "delete_schedule to mutate nodes, connect_schedules/disconnect_schedules for links, " +
  "validate to get current errors, and replace_model to swap the whole graph. Every " +
  "mutation runs the editor's real validation against the proposed model; a change that " +
  "introduces a validation error is rejected with a reason and nothing is applied. " +
  "Viewport/selection tools help the user SEE what you mean without changing the model: " +
  "focus_schedule centers + selects + opens a schedule's details; set_graph_selection " +
  "highlights one-or-more schedules/links; fit_view frames the whole graph or a subset; " +
  "set_canvas_view/get_canvas_view pan-zoom the canvas; auto_layout_graph re-runs the " +
  "dagre layout. These reject (never throw) on unknown ids and never mutate the graph.";

// Fields a caller may set on a schedule. parents/children/_inheritedResources are
// derived and intentionally excluded.
const UPDATABLE_FIELDS = [
  'name', 'time', 'group', 'purpose', 'priority', 'isActive', 'direction',
  'tags', 'attributes', 'configuration', 'securityLevelId', 'followUp',
  'phaseOut', 'resources', 'approvalGroups', 'locationGroup', 'requirements',
] as const;

const ok = (result?: unknown): EditorToolResult => ({ ok: true, result });
const reject = (reason: string): EditorToolResult => ({ ok: false, rejected: true, reason });

/** Compact projection of a schedule for read tools (drops runtime/derived noise). */
function compactSchedule(s: Schedule) {
  return {
    scheduleId: s.scheduleId,
    name: s.name,
    isActive: s.isActive,
    group: s.group,
    purpose: s.purpose,
    time: s.time,
    priority: s.priority,
    direction: s.direction,
    resourceCount: Array.isArray(s.resources) ? s.resources.length : 0,
    requirementCount: Array.isArray(s.requirements) ? s.requirements.length : 0,
    tags: s.tags || [],
  };
}

/** Pure validation of the current graph — returns a list of human-readable errors. */
export function validateGraph(nodes: Node<Schedule>[], edges: Edge[]): string[] {
  const errors: string[] = [];
  const ids = new Set<string>();

  nodes.forEach((n) => {
    const s = n.data;
    if (!s.scheduleId) errors.push(`Node "${n.id}" is missing scheduleId.`);
    if (ids.has(s.scheduleId)) {
      errors.push(`Duplicate scheduleId "${s.scheduleId}".`);
    }
    ids.add(s.scheduleId);
    if (!s.name || !String(s.name).trim()) {
      errors.push(`Schedule "${s.scheduleId}" has an empty name.`);
    }
    if (typeof s.priority !== 'number') {
      errors.push(`Schedule "${s.name || s.scheduleId}" has a non-numeric priority.`);
    }
  });

  edges.forEach((e) => {
    if (!ids.has(e.source)) {
      errors.push(`Edge "${e.id}" references unknown source node "${e.source}".`);
    }
    if (!ids.has(e.target)) {
      errors.push(`Edge "${e.id}" references unknown target node "${e.target}".`);
    }
    if (e.source === e.target) {
      errors.push(`Edge "${e.id}" is a self-loop on "${e.source}".`);
    }
  });

  // Cycle detection: re-derive by removing each edge and re-testing it as an addition.
  for (let i = 0; i < edges.length; i++) {
    const e = edges[i];
    const rest = edges.filter((_, idx) => idx !== i);
    if (isCyclic(rest, { source: e.source, target: e.target, sourceHandle: null, targetHandle: null })) {
      errors.push(`Edge "${e.source}->${e.target}" participates in a cycle.`);
      break;
    }
  }

  return errors;
}

/**
 * Returns the validation errors a candidate model INTRODUCES relative to the
 * current model. A pre-existing error (already present before the change) is not
 * the new mutation's fault, so it is not counted — only genuinely new errors
 * cause a rejection.
 */
function introducedErrors(
  beforeNodes: Node<Schedule>[],
  beforeEdges: Edge[],
  afterNodes: Node<Schedule>[],
  afterEdges: Edge[],
): string[] {
  const baseline = new Set(validateGraph(beforeNodes, beforeEdges));
  return validateGraph(afterNodes, afterEdges).filter((e) => !baseline.has(e));
}

/**
 * Validate a candidate field value for a schedule update. Returns an error
 * string when the value is malformed, or null when it is acceptable.
 *
 * Covers the structured fields (locationGroup / approvalGroups / resources /
 * requirements) in addition to the scalar fields, so the AI cannot write a
 * shape the rest of the editor would choke on.
 */
function validateFieldValue(field: string, value: unknown): string | null {
  const isObj = (v: unknown): v is Record<string, unknown> =>
    typeof v === 'object' && v !== null && !Array.isArray(v);

  switch (field) {
    case 'name':
      if (!value || !String(value).trim()) return '"name" cannot be empty.';
      return null;
    case 'priority':
      if (typeof value !== 'number') return '"priority" must be a number.';
      return null;
    case 'direction':
      if (value !== 0 && value !== 1) return '"direction" must be 0 (BottomUp) or 1 (TopDown).';
      return null;
    case 'isActive':
      if (typeof value !== 'boolean') return '"isActive" must be a boolean.';
      return null;
    case 'tags':
      if (!Array.isArray(value) || value.some((t) => typeof t !== 'string')) {
        return '"tags" must be an array of strings.';
      }
      return null;
    case 'time':
    case 'group':
    case 'purpose':
      if (value != null && typeof value !== 'string') return `"${field}" must be a string.`;
      return null;
    case 'securityLevelId':
    case 'followUp':
      if (value != null && typeof value !== 'string') return `"${field}" must be a string or null.`;
      return null;
    case 'attributes':
      if (value !== null && (!Array.isArray(value) || value.some((a) => typeof a !== 'string'))) {
        return '"attributes" must be an array of strings or null.';
      }
      return null;
    case 'configuration':
      if (!isObj(value)) return '"configuration" must be an object.';
      return null;
    case 'locationGroup': {
      const lg = value as Partial<LocationGroup>;
      if (!isObj(value) || typeof lg.groupId !== 'string' || typeof lg.nodeId !== 'string') {
        return '"locationGroup" must be an object with string "groupId" and "nodeId".';
      }
      return null;
    }
    case 'approvalGroups': {
      if (!Array.isArray(value)) return '"approvalGroups" must be an array.';
      for (const g of value as Partial<ApprovalGroup>[]) {
        if (!isObj(g) || typeof g.groupId !== 'string' || typeof g.nodeId !== 'string' || typeof g.type !== 'string') {
          return 'Each "approvalGroups" entry needs string "groupId", "nodeId" and "type".';
        }
      }
      return null;
    }
    case 'resources': {
      if (!Array.isArray(value)) return '"resources" must be an array.';
      for (const r of value as Partial<ResourceRef>[]) {
        if (!isObj(r) || typeof r.systemId !== 'string' || typeof r.resourceId !== 'string') {
          return 'Each "resources" entry needs string "systemId" and "resourceId".';
        }
        if (r.resourcePlanId != null && typeof r.resourcePlanId !== 'string') {
          return '"resources" entry "resourcePlanId" must be a string or null.';
        }
      }
      return null;
    }
    case 'requirements': {
      if (!Array.isArray(value)) return '"requirements" must be an array.';
      for (const req of value as Partial<ScheduleRequirement>[]) {
        if (!isObj(req) || typeof req.testamentTypeId !== 'string' || !req.testamentTypeId.trim()) {
          return 'Each "requirements" entry needs a non-empty string "testamentTypeId".';
        }
      }
      return null;
    }
    case 'phaseOut': {
      if (value === null) return null;
      const p = value as Record<string, unknown>;
      if (!isObj(value) || typeof p.scheduleId !== 'string' || typeof p.duration !== 'string') {
        return '"phaseOut" must be null or an object with string "scheduleId" and "duration".';
      }
      return null;
    }
    default:
      return null;
  }
}

const TOOLS: EditorAiTool[] = [
  {
    name: 'get_model',
    description:
      'Return the current entitlement graph as compact JSON: schedules (nodes) and ' +
      'links (parent->child edges). Use this before mutating.',
    parameters: { type: 'object', properties: {}, additionalProperties: false },
  },
  {
    name: 'list_node_types',
    description:
      "List the node/element types this editor supports. This editor has a single " +
      "node type, 'schedule', plus 'link' edges (parent->child). Returns the editable " +
      'fields of a schedule.',
    parameters: { type: 'object', properties: {}, additionalProperties: false },
  },
  {
    name: 'add_schedule',
    description:
      'Add a new schedule (permission node). Provide name and any optional fields. ' +
      'Returns the generated scheduleId. parents/children are derived from links and ' +
      'cannot be set here. Rejected if the resulting model fails validation.',
    parameters: {
      type: 'object',
      properties: {
        name: { type: 'string', description: 'Display name (required).' },
        group: { type: 'string' },
        purpose: { type: 'string' },
        time: { type: 'string', description: "e.g. '24/7' or '08:00-18:00'." },
        priority: { type: 'number' },
        direction: {
          type: 'number',
          enum: [0, 1],
          description: '0 = BottomUp (inherit from parents), 1 = TopDown (push to children).',
        },
        isActive: { type: 'boolean' },
        tags: { type: 'array', items: { type: 'string' } },
      },
      required: ['name'],
      additionalProperties: false,
    },
  },
  {
    name: 'update_schedule',
    description:
      'Update one field of an existing schedule. Rejects unknown scheduleId, ' +
      'attempts to set derived fields (parents/children), unknown fields, malformed ' +
      'values, or any change that introduces a validation error.',
    parameters: {
      type: 'object',
      properties: {
        scheduleId: { type: 'string' },
        field: { type: 'string', enum: [...UPDATABLE_FIELDS] },
        value: { description: 'New value for the field (type depends on the field).' },
      },
      required: ['scheduleId', 'field', 'value'],
      additionalProperties: false,
    },
  },
  {
    name: 'delete_schedule',
    description: 'Delete a schedule and all links touching it. Rejects unknown scheduleId.',
    parameters: {
      type: 'object',
      properties: { scheduleId: { type: 'string' } },
      required: ['scheduleId'],
      additionalProperties: false,
    },
  },
  {
    name: 'connect_schedules',
    description:
      'Link a parent schedule to a child schedule (source=parent, target=child). ' +
      'Rejected if either id is unknown, the link already exists, or it would create a cycle.',
    parameters: {
      type: 'object',
      properties: {
        parentId: { type: 'string', description: 'Source / parent scheduleId.' },
        childId: { type: 'string', description: 'Target / child scheduleId.' },
      },
      required: ['parentId', 'childId'],
      additionalProperties: false,
    },
  },
  {
    name: 'disconnect_schedules',
    description: 'Remove the parent->child link. Rejects if the link does not exist.',
    parameters: {
      type: 'object',
      properties: {
        parentId: { type: 'string' },
        childId: { type: 'string' },
      },
      required: ['parentId', 'childId'],
      additionalProperties: false,
    },
  },
  {
    name: 'replace_model',
    description:
      'Replace the entire graph with a new set of schedules and links. Rejected (with no ' +
      'mutation) if the proposed model fails validation.',
    parameters: {
      type: 'object',
      properties: {
        schedules: {
          type: 'array',
          description: 'Full list of schedules. Each needs at least a name.',
          items: {
            type: 'object',
            properties: {
              scheduleId: { type: 'string' },
              name: { type: 'string' },
              group: { type: 'string' },
              purpose: { type: 'string' },
              time: { type: 'string' },
              priority: { type: 'number' },
              direction: { type: 'number', enum: [0, 1] },
              isActive: { type: 'boolean' },
              tags: { type: 'array', items: { type: 'string' } },
            },
            required: ['name'],
          },
        },
        links: {
          type: 'array',
          description: 'Parent->child links between schedules.',
          items: {
            type: 'object',
            properties: {
              parentId: { type: 'string' },
              childId: { type: 'string' },
            },
            required: ['parentId', 'childId'],
          },
        },
      },
      required: ['schedules'],
      additionalProperties: false,
    },
  },
  {
    name: 'validate',
    description:
      'Run the editor validation over the current graph and return the list of errors ' +
      '(empty array = valid).',
    parameters: { type: 'object', properties: {}, additionalProperties: false },
  },

  // --- Viewport / selection tools (non-mutating UI affordances) ---
  {
    name: 'get_canvas_view',
    description:
      'Return the current canvas viewport ({x,y,zoom}) plus the scheduleIds that are ' +
      'currently selected/highlighted. Read-only; does not change the graph or the view.',
    parameters: { type: 'object', properties: {}, additionalProperties: false },
  },
  {
    name: 'set_canvas_view',
    description:
      'Pan and/or zoom the canvas to an exact viewport. Any omitted axis keeps its ' +
      'current value. Zoom is clamped to the editor bounds (0.1–1.5). Does not mutate the graph. ' +
      'Rejects gracefully if the live canvas is unavailable.',
    parameters: {
      type: 'object',
      properties: {
        x: { type: 'number', description: 'Viewport x translation (world px).' },
        y: { type: 'number', description: 'Viewport y translation (world px).' },
        zoom: { type: 'number', description: 'Zoom factor; clamped to [0.1, 1.5].' },
        durationMs: { type: 'number', description: 'Animation duration in ms (0 = instant).' },
      },
      additionalProperties: false,
    },
  },
  {
    name: 'fit_view',
    description:
      'Fit the whole graph into view, or just the given schedules when "scheduleIds" is ' +
      'provided. Rejects if any provided scheduleId is unknown. Does not mutate the graph.',
    parameters: {
      type: 'object',
      properties: {
        scheduleIds: {
          type: 'array',
          items: { type: 'string' },
          description: 'Optional subset to frame. Omit/empty to fit the entire graph.',
        },
        padding: { type: 'number', description: 'Fractional padding around the bounds (e.g. 0.2).' },
        maxZoom: { type: 'number', description: 'Upper zoom clamp while fitting (<= 1.5).' },
        durationMs: { type: 'number', description: 'Animation duration in ms (0 = instant).' },
      },
      additionalProperties: false,
    },
  },
  {
    name: 'focus_schedule',
    description:
      'Center the canvas on one schedule, select it, and open its details panel — the ' +
      'way to point the user at a specific node. Rejects an unknown scheduleId. Does not mutate the graph.',
    parameters: {
      type: 'object',
      properties: {
        scheduleId: { type: 'string', description: 'The schedule to focus (required).' },
        zoom: { type: 'number', description: 'Zoom to center at; clamped to [0.1, 1.5]. Default 1.2.' },
        durationMs: { type: 'number', description: 'Animation duration in ms (0 = instant).' },
      },
      required: ['scheduleId'],
      additionalProperties: false,
    },
  },
  {
    name: 'set_graph_selection',
    description:
      'Highlight a set of schedules (and optionally parent->child links) via the canvas ' +
      'selection state, so the user sees exactly which nodes you mean. Pass empty arrays to ' +
      'clear the selection. Rejects if any scheduleId or link endpoint is unknown. Does not mutate the graph.',
    parameters: {
      type: 'object',
      properties: {
        scheduleIds: {
          type: 'array',
          items: { type: 'string' },
          description: 'Schedules to highlight. Empty array clears node selection.',
        },
        links: {
          type: 'array',
          description: 'Parent->child links to highlight.',
          items: {
            type: 'object',
            properties: {
              parentId: { type: 'string' },
              childId: { type: 'string' },
            },
            required: ['parentId', 'childId'],
          },
        },
        fit: { type: 'boolean', description: 'When true, also fit the selected schedules into view.' },
      },
      additionalProperties: false,
    },
  },
  {
    name: 'auto_layout_graph',
    description:
      'Re-run the editor\'s automatic (dagre) layout to tidy node positions. This only ' +
      'repositions nodes; it does not add/remove/edit schedules or links. Optionally refits the view.',
    parameters: {
      type: 'object',
      properties: {
        fitAfter: { type: 'boolean', description: 'Fit the whole graph into view after layout (default true).' },
        durationMs: { type: 'number', description: 'Fit animation duration in ms (0 = instant).' },
      },
      additionalProperties: false,
    },
  },
];

/**
 * Build the EditorAiCapability from the live model + the editor's real
 * mutation/validation paths. The returned object is stable; the deps' getters
 * are ref-backed by the caller so every invoke reads the LATEST model.
 */
export function buildAiCapability(deps: AiCapabilityDeps): EditorAiCapability {
  const findNode = (id: unknown): Node<Schedule> | undefined =>
    deps.getNodes().find((n) => n.id === id || n.data.scheduleId === id);

  const bounds = deps.zoomBounds ?? { min: 0.1, max: 1.5 };
  const clampZoom = (z: number): number => Math.min(bounds.max, Math.max(bounds.min, z));

  const invoke = async (
    toolName: string,
    args: Record<string, unknown>,
  ): Promise<EditorToolResult> => {
    const a = args || {};
    try {
      switch (toolName) {
        case 'get_model': {
          const nodes = deps.getNodes();
          const edges = deps.getEdges();
          return ok({
            schedules: nodes.map((n) => compactSchedule(n.data)),
            links: edges.map((e) => ({ parentId: e.source, childId: e.target })),
          });
        }

        case 'list_node_types': {
          return ok({
            nodeTypes: [
              {
                type: 'schedule',
                description: 'A permission schedule node.',
                fields: [...UPDATABLE_FIELDS],
                derived: ['parents', 'children', '_inheritedResources'],
              },
            ],
            edgeType: { type: 'link', description: 'Directed parent->child link; graph stays acyclic.' },
          });
        }

        case 'add_schedule': {
          if (!a.name || !String(a.name).trim()) {
            return reject('add_schedule requires a non-empty "name".');
          }
          if (a.direction !== undefined && a.direction !== 0 && a.direction !== 1) {
            return reject('"direction" must be 0 (BottomUp) or 1 (TopDown).');
          }
          const nodes = deps.getNodes();
          const edges = deps.getEdges();
          const newNode = deps.buildScheduleNode({
            name: String(a.name),
            group: a.group as string | undefined,
            purpose: a.purpose as string | undefined,
            time: a.time as string | undefined,
            priority: typeof a.priority === 'number' ? a.priority : undefined,
            direction: a.direction as Direction | undefined,
            isActive: typeof a.isActive === 'boolean' ? a.isActive : undefined,
            tags: Array.isArray(a.tags) ? (a.tags as string[]) : undefined,
          });
          const nextNodes = [...nodes, newNode];
          const introduced = introducedErrors(nodes, edges, nextNodes, edges);
          if (introduced.length) return reject(introduced[0]);
          deps.applyModel(nextNodes, edges);
          return ok({ scheduleId: newNode.data.scheduleId });
        }

        case 'update_schedule': {
          const { scheduleId, field, value } = a as {
            scheduleId?: unknown; field?: unknown; value?: unknown;
          };
          if (!scheduleId) return reject('update_schedule requires "scheduleId".');
          const target = findNode(scheduleId);
          if (!target) {
            return reject(`Unknown scheduleId "${String(scheduleId)}".`);
          }
          if (field === 'parents' || field === 'children') {
            return reject(
              `Field "${String(field)}" is derived from links; use connect_schedules / disconnect_schedules instead.`,
            );
          }
          if (!UPDATABLE_FIELDS.includes(field as typeof UPDATABLE_FIELDS[number])) {
            return reject(
              `Unknown or non-editable field "${String(field)}". Editable: ${UPDATABLE_FIELDS.join(', ')}.`,
            );
          }
          const shapeError = validateFieldValue(field as string, value);
          if (shapeError) return reject(shapeError);

          const nodes = deps.getNodes();
          const edges = deps.getEdges();
          const nextNodes = nodes.map((n) =>
            n.id === target.id
              ? { ...n, data: { ...n.data, [field as string]: value } }
              : n,
          );
          const introduced = introducedErrors(nodes, edges, nextNodes, edges);
          if (introduced.length) return reject(introduced[0]);
          deps.applyModel(nextNodes, edges);
          return ok({ scheduleId, field, value });
        }

        case 'delete_schedule': {
          const { scheduleId } = a as { scheduleId?: unknown };
          if (!scheduleId) return reject('delete_schedule requires "scheduleId".');
          const target = findNode(scheduleId);
          if (!target) {
            return reject(`Unknown scheduleId "${String(scheduleId)}".`);
          }
          const nodes = deps.getNodes();
          const edges = deps.getEdges();
          const nextNodes = nodes.filter((n) => n.id !== target.id);
          const nextEdges = edges.filter((e) => e.source !== target.id && e.target !== target.id);
          const introduced = introducedErrors(nodes, edges, nextNodes, nextEdges);
          if (introduced.length) return reject(introduced[0]);
          deps.applyModel(nextNodes, nextEdges);
          return ok({ deleted: scheduleId });
        }

        case 'connect_schedules': {
          const { parentId, childId } = a as { parentId?: unknown; childId?: unknown };
          if (!parentId || !childId) {
            return reject('connect_schedules requires "parentId" and "childId".');
          }
          const parent = findNode(parentId);
          const child = findNode(childId);
          if (!parent) return reject(`Unknown parentId "${String(parentId)}".`);
          if (!child) return reject(`Unknown childId "${String(childId)}".`);
          const nodes = deps.getNodes();
          const edges = deps.getEdges();
          if (edges.some((e) => e.source === parent.id && e.target === child.id)) {
            return reject('Connection rejected: this link already exists.');
          }
          const nextEdges = [...edges, deps.makeEdge(parent.id, child.id)];
          const introduced = introducedErrors(nodes, edges, nodes, nextEdges);
          if (introduced.length) return reject(introduced[0]);
          deps.applyModel(nodes, nextEdges);
          return ok({ parentId, childId });
        }

        case 'disconnect_schedules': {
          const { parentId, childId } = a as { parentId?: unknown; childId?: unknown };
          if (!parentId || !childId) {
            return reject('disconnect_schedules requires "parentId" and "childId".');
          }
          const parent = findNode(parentId);
          const child = findNode(childId);
          const sourceId = parent ? parent.id : String(parentId);
          const targetId = child ? child.id : String(childId);
          const nodes = deps.getNodes();
          const edges = deps.getEdges();
          const exists = edges.some((e) => e.source === sourceId && e.target === targetId);
          if (!exists) {
            return reject(`No link from "${String(parentId)}" to "${String(childId)}".`);
          }
          const nextEdges = edges.filter((e) => !(e.source === sourceId && e.target === targetId));
          deps.applyModel(nodes, nextEdges);
          return ok({ parentId, childId });
        }

        case 'replace_model': {
          const schedules = a.schedules;
          if (!Array.isArray(schedules)) {
            return reject('replace_model requires a "schedules" array.');
          }
          const links = Array.isArray(a.links) ? (a.links as Array<Record<string, unknown>>) : [];

          // Build candidate nodes (deterministic ids; honor a provided scheduleId).
          const nextNodes: Node<Schedule>[] = [];
          for (const raw of schedules as Array<Record<string, unknown>>) {
            if (!raw || typeof raw !== 'object') {
              return reject('Each schedule in "schedules" must be an object.');
            }
            if (raw.direction !== undefined && raw.direction !== 0 && raw.direction !== 1) {
              return reject('Each schedule "direction" must be 0 (BottomUp) or 1 (TopDown).');
            }
            nextNodes.push(
              deps.buildScheduleNode({
                scheduleId: typeof raw.scheduleId === 'string' ? raw.scheduleId : undefined,
                name: raw.name as string | undefined,
                group: raw.group as string | undefined,
                purpose: raw.purpose as string | undefined,
                time: raw.time as string | undefined,
                priority: typeof raw.priority === 'number' ? raw.priority : undefined,
                direction: raw.direction as Direction | undefined,
                isActive: typeof raw.isActive === 'boolean' ? raw.isActive : undefined,
                tags: Array.isArray(raw.tags) ? (raw.tags as string[]) : undefined,
              }),
            );
          }

          const idByScheduleId = new Map<string, string>();
          nextNodes.forEach((n) => idByScheduleId.set(n.data.scheduleId, n.id));

          const nextEdges: Edge[] = [];
          for (const l of links) {
            const parentId = l.parentId;
            const childId = l.childId;
            if (typeof parentId !== 'string' || typeof childId !== 'string') {
              return reject('Each link needs string "parentId" and "childId".');
            }
            const src = idByScheduleId.get(parentId);
            const tgt = idByScheduleId.get(childId);
            if (!src) return reject(`Link references unknown parentId "${parentId}".`);
            if (!tgt) return reject(`Link references unknown childId "${childId}".`);
            nextEdges.push(deps.makeEdge(src, tgt));
          }

          // replace_model is a full swap: the whole candidate must be valid.
          const errors = validateGraph(nextNodes, nextEdges);
          if (errors.length) return reject(errors[0]);
          deps.applyModel(nextNodes, nextEdges);
          return ok({ scheduleCount: nextNodes.length, linkCount: nextEdges.length });
        }

        case 'validate': {
          const errors = validateGraph(deps.getNodes(), deps.getEdges());
          return ok({ valid: errors.length === 0, errors });
        }

        case 'get_canvas_view': {
          const vp = deps.getViewport ? deps.getViewport() : null;
          const selected = deps
            .getNodes()
            .filter((n) => n.selected)
            .map((n) => n.data.scheduleId);
          const selectedLinks = deps
            .getEdges()
            .filter((e) => e.selected)
            .map((e) => {
              const p = deps.getNodes().find((n) => n.id === e.source);
              const c = deps.getNodes().find((n) => n.id === e.target);
              return { parentId: p?.data.scheduleId ?? e.source, childId: c?.data.scheduleId ?? e.target };
            });
          return ok({ viewport: vp, selectedScheduleIds: selected, selectedLinks });
        }

        case 'set_canvas_view': {
          if (!deps.setViewport || !deps.getViewport) {
            return reject('Canvas viewport is not available in this editor instance.');
          }
          const cur = deps.getViewport();
          const { x, y, zoom, durationMs } = a as {
            x?: unknown; y?: unknown; zoom?: unknown; durationMs?: unknown;
          };
          if (x !== undefined && typeof x !== 'number') return reject('"x" must be a number.');
          if (y !== undefined && typeof y !== 'number') return reject('"y" must be a number.');
          if (zoom !== undefined && typeof zoom !== 'number') return reject('"zoom" must be a number.');
          const next = {
            x: typeof x === 'number' ? x : cur.x,
            y: typeof y === 'number' ? y : cur.y,
            zoom: typeof zoom === 'number' ? clampZoom(zoom) : cur.zoom,
          };
          deps.setViewport(next, { duration: typeof durationMs === 'number' ? durationMs : 0 });
          return ok({ viewport: next });
        }

        case 'fit_view': {
          if (!deps.fitView) {
            return reject('Canvas fit-view is not available in this editor instance.');
          }
          const { scheduleIds, padding, maxZoom, durationMs } = a as {
            scheduleIds?: unknown; padding?: unknown; maxZoom?: unknown; durationMs?: unknown;
          };
          let nodeFilter: { id: string }[] | undefined;
          if (Array.isArray(scheduleIds) && scheduleIds.length > 0) {
            nodeFilter = [];
            for (const sid of scheduleIds) {
              const node = findNode(sid);
              if (!node) return reject(`Unknown scheduleId "${String(sid)}".`);
              nodeFilter.push({ id: node.id });
            }
          }
          deps.fitView({
            nodes: nodeFilter,
            padding: typeof padding === 'number' ? padding : undefined,
            maxZoom: typeof maxZoom === 'number' ? clampZoom(maxZoom) : undefined,
            duration: typeof durationMs === 'number' ? durationMs : 0,
          });
          return ok({ fitted: nodeFilter ? nodeFilter.length : 'all' });
        }

        case 'focus_schedule': {
          const { scheduleId, zoom, durationMs } = a as {
            scheduleId?: unknown; zoom?: unknown; durationMs?: unknown;
          };
          if (!scheduleId) return reject('focus_schedule requires "scheduleId".');
          const node = findNode(scheduleId);
          if (!node) return reject(`Unknown scheduleId "${String(scheduleId)}".`);
          if (zoom !== undefined && typeof zoom !== 'number') return reject('"zoom" must be a number.');

          // Select the node + open the details panel.
          deps.setSelection?.([node.id], []);
          deps.openDetailsFor?.(node.data);

          // Center the viewport on the node's center.
          if (deps.setCenter) {
            const width = (node.width as number | undefined) ?? 0;
            const height = (node.height as number | undefined) ?? 0;
            const cx = node.position.x + width / 2;
            const cy = node.position.y + height / 2;
            deps.setCenter(cx, cy, {
              zoom: clampZoom(typeof zoom === 'number' ? zoom : 1.2),
              duration: typeof durationMs === 'number' ? durationMs : 400,
            });
          } else if (deps.fitView) {
            deps.fitView({ nodes: [{ id: node.id }], duration: 400 });
          }
          return ok({ focused: node.data.scheduleId });
        }

        case 'set_graph_selection': {
          if (!deps.setSelection) {
            return reject('Canvas selection is not available in this editor instance.');
          }
          const { scheduleIds, links, fit } = a as {
            scheduleIds?: unknown; links?: unknown; fit?: unknown;
          };
          const nodeIds: string[] = [];
          if (scheduleIds !== undefined) {
            if (!Array.isArray(scheduleIds)) return reject('"scheduleIds" must be an array.');
            for (const sid of scheduleIds) {
              const node = findNode(sid);
              if (!node) return reject(`Unknown scheduleId "${String(sid)}".`);
              nodeIds.push(node.id);
            }
          }
          const edgeIds: string[] = [];
          if (links !== undefined) {
            if (!Array.isArray(links)) return reject('"links" must be an array.');
            const edges = deps.getEdges();
            for (const raw of links as Array<Record<string, unknown>>) {
              const parent = findNode(raw?.parentId);
              const child = findNode(raw?.childId);
              if (!parent) return reject(`Unknown parentId "${String(raw?.parentId)}".`);
              if (!child) return reject(`Unknown childId "${String(raw?.childId)}".`);
              const edge = edges.find((e) => e.source === parent.id && e.target === child.id);
              if (!edge) {
                return reject(`No link from "${String(raw?.parentId)}" to "${String(raw?.childId)}".`);
              }
              edgeIds.push(edge.id);
            }
          }
          deps.setSelection(nodeIds, edgeIds);
          if (fit === true && nodeIds.length && deps.fitView) {
            deps.fitView({ nodes: nodeIds.map((id) => ({ id })), duration: 400 });
          }
          return ok({ selectedScheduleIds: nodeIds.map((id) => findNode(id)?.data.scheduleId ?? id), selectedLinkCount: edgeIds.length });
        }

        case 'auto_layout_graph': {
          if (!deps.runLayout) {
            return reject('Auto-layout is not available in this editor instance.');
          }
          const { fitAfter, durationMs } = a as { fitAfter?: unknown; durationMs?: unknown };
          deps.runLayout();
          if (fitAfter !== false && deps.fitView) {
            deps.fitView({ duration: typeof durationMs === 'number' ? durationMs : 400 });
          }
          return ok({ relayout: true });
        }

        default:
          return reject(`Unknown tool "${toolName}".`);
      }
    } catch (e) {
      return reject(`Tool "${toolName}" threw: ${(e as Error)?.message ?? String(e)}`);
    }
  };

  return { kind: KIND, guidelines: GUIDELINES, tools: TOOLS, invoke };
}

/**
 * React hook: builds the capability once (deps are stable, ref-backed controller
 * callbacks) and publishes it to the host via an effect. Calls onReady(null) on
 * unmount and unregisters the SAME capability instance. Uses a ref for onReady so
 * the publish does NOT re-fire on every render — only when the capability changes.
 */
export function useAiCapability(
  deps: AiCapabilityDeps,
  onReady?: (cap: EditorAiCapability | null) => void,
): EditorAiCapability {
  // deps.getNodes/getEdges are ref-backed by the caller, so the capability is stable.
  const capability = useMemo(() => buildAiCapability(deps), []); // eslint-disable-line react-hooks/exhaustive-deps

  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  useEffect(() => {
    onReadyRef.current?.(capability);
    return () => onReadyRef.current?.(null);
  }, [capability]);

  return capability;
}
